/**
 * The catalog's read model.
 *
 * Pure functions over rows already loaded from IndexedDB — no React, no I/O —
 * so the rules the screens are judged on can be tested without a DOM. The
 * screens render what these functions return and add nothing numeric of their
 * own.
 *
 * Three rules from the plan live here rather than in the components, because a
 * component is a bad place to keep a rule:
 *
 *  1. **Sessions weigh equally.** `gameSummary` builds one `Distribution` per
 *     completed session and hands those to `gameHeadline`. It never pools raw
 *     ratings, so a six-player night cannot outvote a two-player night. That is
 *     the single most load-bearing call in this file.
 *  2. **The overall is a figure about a night, never about a game's aspects.**
 *     Each seat's overall is the mean of the topics *that seat* rated, or the
 *     number they set over it, and it is built here per session and then weighed
 *     one night to one night like the headline. What is still not built anywhere
 *     is a score for an *aspect* rolled into a score for a game: aspects stay
 *     profiles carrying a modal label, an `n` and a spread, and a function that
 *     turned those into a number would still be the bug.
 *  3. **Rubric versions are segregated, not merged.** A game's aspect profiles
 *     are built from the sessions sharing its newest `rubric_version` only.
 *     Older versions are reported as a count so the screen can say so out loud
 *     instead of silently averaging two different questionnaires together.
 */

import {
  aspectProfile,
  disagreement,
  distribution,
  gameHeadline,
  overallValue,
  sessionHeadline,
  topicValue,
  type AspectProfile,
  type AspectSlice,
  type Disagreement,
  type Distribution,
  type RatingCell,
  type TopicContribution,
  type TopicValue,
} from "../../../shared/aggregate.ts";
import type {
  AspectKind,
  AspectRating,
  Game,
  HeadlineRating,
  OverallRating,
  PlayAgain,
  PlaySession,
  Profile,
  SessionAspect,
  SessionParticipant,
  SessionSubQuestion,
  Stop,
  SubQuestionRating,
} from "../../../shared/types.ts";

// ---------------------------------------------------------------------------
// The local store, as the screens see it
// ---------------------------------------------------------------------------

/** Everything the catalog reads. Loaded once per screen from IndexedDB. */
export interface Library {
  games: readonly Game[];
  sessions: readonly PlaySession[];
  participants: readonly SessionParticipant[];
  headlines: readonly HeadlineRating[];
  sessionAspects: readonly SessionAspect[];
  aspectRatings: readonly AspectRating[];
  /**
   * The follow-ups, and the answers to them. Here because a topic's value is not
   * always in `aspectRatings`: a rater who went into detail instead of rating the
   * topic has no row there at all, and reading only that table would drop their
   * whole night out of this game's figures without anything looking wrong.
   */
  sessionSubQuestions: readonly SessionSubQuestion[];
  subquestionRatings: readonly SubQuestionRating[];
  /**
   * Overrides only, and usually empty. A seat that let the topics work its
   * overall out writes nothing here, so this table can never be counted to find
   * out who rated a night — it says who disagreed with the arithmetic.
   */
  overallRatings: readonly OverallRating[];
  playAgain: readonly PlayAgain[];
  profiles: readonly Profile[];
}

export const EMPTY_LIBRARY: Library = {
  games: [],
  sessions: [],
  participants: [],
  headlines: [],
  sessionAspects: [],
  aspectRatings: [],
  sessionSubQuestions: [],
  subquestionRatings: [],
  overallRatings: [],
  playAgain: [],
  profiles: [],
};

/**
 * `pull` deletes tombstoned rows locally, but a row tombstoned on this device
 * is still sitting in the store until the server answers. Every read filters.
 */
function live<T extends { deleted: 0 | 1 }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.deleted !== 1);
}

function byKey<T>(rows: readonly T[], key: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const bucket = out.get(k);
    if (bucket) bucket.push(row);
    else out.set(k, [row]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export interface SessionSummary {
  session: PlaySession;
  /** Who was at the table, in the order they were added. */
  players: SessionParticipant[];
  /** The night's "desire to play again" spread. `n` is the number of raters. */
  headline: Distribution;
  /**
   * How good the evening was, one figure per seat. `n` is the number of raters
   * with anything to say — which is not the same set as the headline's, because
   * the headline is compulsory and this is worked out from topics anybody may
   * skip.
   */
  overall: Distribution;
}

/**
 * Every seat's overall, per session: the number they set, or the mean of the
 * topics they rated.
 *
 * Built here rather than read from a table because the derived case stores
 * nothing at all — `overallRatings` holds overrides only. Counting that table
 * would report a night everybody rated as a night nobody rated, and would do it
 * without anything looking wrong, which is the same trap `topicValuesBySnapshot`
 * exists to avoid one level down.
 *
 * Seats come from the ratings rather than from `session_participant`, so a seat
 * that sat at the table and rated nothing is absent instead of being a null in
 * the distribution. Silence is not a low score.
 *
 * `sessions` is passed in rather than read off the library so this walks the
 * nights whose answer is kept — the caller has already dropped the drafts, and
 * deriving every seat's overall for a night that is still being rated is work
 * thrown away immediately afterwards.
 */
function overallValuesBySession(
  lib: Library,
  topics: Map<string, Map<string, TopicValue>>,
  sessions: readonly PlaySession[],
): Map<string, number[]> {
  const aspectsBySession = byKey(live(lib.sessionAspects), (sa) => sa.session_id);
  const overrides = byKey(live(lib.overallRatings), (r) => r.session_id);

  const out = new Map<string, number[]>();
  for (const session of sessions) {
    const perSeat = new Map<string, TopicContribution[]>();

    for (const snapshot of aspectsBySession.get(session.id) ?? []) {
      for (const [participantId, value] of topics.get(snapshot.id) ?? []) {
        const list = perSeat.get(participantId) ?? [];
        list.push({ kind: snapshot.kind_snapshot, value });
        perSeat.set(participantId, list);
      }
    }

    const set = new Map((overrides.get(session.id) ?? []).map((r) => [r.participant_id, r.value]));
    const values: number[] = [];
    for (const participantId of new Set([...perSeat.keys(), ...set.keys()])) {
      const derived = overallValue(set.get(participantId) ?? null, perSeat.get(participantId) ?? []);
      if (derived.value === null) continue;
      values.push(derived.value);
    }
    out.set(session.id, values);
  }
  return out;
}

/**
 * Completed sessions only, newest first.
 *
 * Drafts are excluded from every aggregate deliberately: a session where two of
 * five people have typed a number is not a weaker signal, it is a half-finished
 * one, and showing it would let the feed's headline move while the group is
 * still arguing.
 *
 * `topicsIn` is an optimisation with one caller: `buildGameDetail` needs the
 * same per-seat topic values for its aspect blocks, and walking every rating in
 * the library twice to build them is the whole cost of this function.
 */
export function completedSessions(
  lib: Library,
  topicsIn?: Map<string, Map<string, TopicValue>>,
): SessionSummary[] {
  const sessions = live(lib.sessions).filter((s) => s.status === "COMPLETED");
  const participants = byKey(live(lib.participants), (p) => p.session_id);
  const headlines = byKey(live(lib.headlines), (h) => h.session_id);
  const overalls = overallValuesBySession(lib, topicsIn ?? topicValuesBySnapshot(lib), sessions);

  return sessions
    .map((session) => ({
      session,
      players: [...(participants.get(session.id) ?? [])].sort(
        (a, b) => a.updated_seq - b.updated_seq,
      ),
      headline: sessionHeadline(headlines.get(session.id) ?? []),
      overall: distribution(overalls.get(session.id) ?? []),
    }))
    .sort((a, b) => b.session.played_at - a.session.played_at);
}

/** Sessions still being rated. The landing screen offers to resume them. */
export function draftSessions(lib: Library): PlaySession[] {
  return live(lib.sessions)
    .filter((s) => s.status === "DRAFT")
    .sort((a, b) => b.played_at - a.played_at);
}

/**
 * Whether the shelf is genuinely empty and may say so.
 *
 * Two things have to be true, and the second is the one that was missing: the
 * store has actually answered — a failed or pending read is not an empty shelf
 * — and there is nothing on the shelf *of either kind*. A night in progress is
 * something the group started tonight, and telling them nothing has been
 * reviewed while offering to resume it directly above reads as a bug.
 */
export function blankShelf(shelf: { ready: boolean; reviewed: number; drafts: number }): boolean {
  return shelf.ready && shelf.reviewed === 0 && shelf.drafts === 0;
}

// ---------------------------------------------------------------------------
// Games
// ---------------------------------------------------------------------------

export interface GameSummary {
  game: Game;
  /** Completed sessions for this game, newest first. */
  sessions: SessionSummary[];
  /**
   * Equal-weighted across sessions; `n` counts NIGHTS, not raters. Null when
   * the group has never finished a review of it — which is not a zero.
   */
  headline: Distribution | null;
  /**
   * The same equal-session weighting, over how good the evenings were. Null on a
   * game whose nights nobody rated a topic on — which is not a zero either.
   */
  overall: Distribution | null;
  lastPlayedAt: number | null;
  plays: number;
  /** The one-line verdict from the most recent night, if the group wrote one. */
  latestVerdict: string | null;
  latestNote: string | null;
}

export function gameSummary(game: Game, sessions: SessionSummary[]): GameSummary {
  const ordered = [...sessions].sort((a, b) => b.session.played_at - a.session.played_at);
  const latest = ordered[0];
  return {
    game,
    sessions: ordered,
    // The equal-session-weight rule, expressed as a call: one distribution per
    // night goes in, so headcount cannot tilt the result.
    headline: gameHeadline(ordered.map((s) => s.headline)),
    // Same call, same rule: one night is one number here too, so a game played
    // once with six people does not outweigh three nights of two.
    overall: gameHeadline(ordered.map((s) => s.overall)),
    lastPlayedAt: latest?.session.played_at ?? null,
    plays: ordered.length,
    latestVerdict: latest?.session.verdict?.trim() || null,
    latestNote: latest?.session.note?.trim() || null,
  };
}

/** Every game the group owns a review of, plus games entered but never played. */
export function summariseGames(lib: Library): GameSummary[] {
  const sessions = byKey(completedSessions(lib), (s) => s.session.game_id);
  return live(lib.games).map((game) => gameSummary(game, sessions.get(game.id) ?? []));
}

/** The feed's order: whatever the group reviewed most recently, first. */
export function byRecency(games: readonly GameSummary[]): GameSummary[] {
  return [...games].sort((a, b) => {
    const at = a.lastPlayedAt;
    const bt = b.lastPlayedAt;
    if (at === bt) return compareTitle(a, b);
    if (at === null) return 1;
    if (bt === null) return -1;
    return bt - at;
  });
}

/** Games the group has actually finished reviewing. The feed shows only these. */
export function reviewed(games: readonly GameSummary[]): GameSummary[] {
  return games.filter((g) => g.plays > 0);
}

// ---------------------------------------------------------------------------
// Shelf: sorting and grouping
//
// There are no filters on aspect values here, and there is no UI hinting at
// them. That is backlog item 1, not a v1 omission to be papered over.
// ---------------------------------------------------------------------------

export type SortMode = "recent" | "plays" | "headline" | "title";
export type GroupMode = "none" | "played" | "players" | "weight" | "title";

export const SORT_LABELS: Readonly<Record<SortMode, string>> = {
  recent: "Last played",
  plays: "Most played",
  headline: "Desire to replay",
  title: "A–Z",
};

export const GROUP_LABELS: Readonly<Record<GroupMode, string>> = {
  none: "One shelf",
  played: "When",
  players: "Player count",
  weight: "Weight",
  title: "Letter",
};

function compareTitle(a: GameSummary, b: GameSummary): number {
  return a.game.title.localeCompare(b.game.title, "en", { sensitivity: "base" });
}

export function sortGames(games: readonly GameSummary[], mode: SortMode): GameSummary[] {
  const list = [...games];
  switch (mode) {
    case "recent":
      return byRecency(list);
    case "plays":
      return list.sort((a, b) => b.plays - a.plays || compareTitle(a, b));
    case "headline":
      // Unrated games sort last rather than as zeroes: "we have not rated this"
      // and "the group did not want to play it again" are different sentences.
      return list.sort((a, b) => {
        const am = a.headline?.mean ?? null;
        const bm = b.headline?.mean ?? null;
        if (am === null && bm === null) return compareTitle(a, b);
        if (am === null) return 1;
        if (bm === null) return -1;
        return bm - am || compareTitle(a, b);
      });
    case "title":
      return list.sort(compareTitle);
  }
}

export interface GameGroup {
  key: string;
  label: string;
  /** Small print under the heading — never a second sort key in disguise. */
  hint: string | null;
  games: GameSummary[];
  /** Groups without a natural place in the order sit at the end. */
  rank: number;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function monthBucket(at: number | null): { key: string; label: string; rank: number } {
  if (at === null) return { key: "never", label: "Not played yet", rank: -1 };
  const d = new Date(at);
  const y = d.getFullYear();
  const m = d.getMonth();
  return {
    key: `${y}-${String(m + 1).padStart(2, "0")}`,
    // Descending months, so a bigger timestamp must produce a smaller rank.
    rank: -(y * 12 + m),
    label: `${MONTHS[m] ?? ""} ${y}`.trim(),
  };
}

function playerBucket(game: Game): { key: string; label: string; rank: number } {
  const max = game.max_players;
  // A hand-entered game often has a minimum and no maximum. That is an unknown
  // ceiling, not a seven-player game, so it is grouped as unknown rather than
  // falling through the bands below.
  if (max === null) return { key: "unknown", label: "Player count unknown", rank: 9 };
  if (max <= 2) return { key: "duel", label: "Two-player", rank: 0 };
  if (max <= 4) return { key: "small", label: "Up to four", rank: 1 };
  if (max <= 6) return { key: "medium", label: "Up to six", rank: 2 };
  return { key: "large", label: "Seven or more", rank: 3 };
}

/**
 * BGG's community weight, banded. This is BGG's number about the game, NOT the
 * group's own Weight aspect — those are different questions and the label says
 * so, because conflating them would be exactly the aspect filter v1 does not
 * ship.
 */
function weightBucket(game: Game): { key: string; label: string; rank: number } {
  const w = game.weight;
  if (w === null) return { key: "unknown", label: "Unrated by BGG", rank: 9 };
  if (w < 1.5) return { key: "filler", label: "Filler", rank: 0 };
  if (w < 2.5) return { key: "light", label: "Light", rank: 1 };
  if (w < 3.5) return { key: "medium", label: "Medium", rank: 2 };
  if (w < 4.5) return { key: "heavy", label: "Heavy", rank: 3 };
  return { key: "brick", label: "Brick", rank: 4 };
}

function letterBucket(game: Game): { key: string; label: string; rank: number } {
  const first = game.title.trim().charAt(0).toUpperCase();
  if (!/[A-Z]/.test(first)) return { key: "#", label: "#", rank: 1 };
  return { key: first, label: first, rank: 0 };
}

const GROUP_HINTS: Readonly<Record<GroupMode, string | null>> = {
  none: null,
  played: null,
  players: "From the box, not from the group",
  weight: "BoardGameGeek's community weight, not the group's Weight aspect",
  title: null,
};

/**
 * Cuts the shelf into labelled sections. `sort` still orders the games inside
 * each one, so the two controls compose instead of overriding each other.
 */
export function groupGames(
  games: readonly GameSummary[],
  group: GroupMode,
  sort: SortMode,
): GameGroup[] {
  const sorted = sortGames(games, sort);
  if (group === "none") {
    return sorted.length === 0
      ? []
      : [{ key: "all", label: "The shelf", hint: null, games: sorted, rank: 0 }];
  }

  const buckets = new Map<string, GameGroup>();
  for (const summary of sorted) {
    const b =
      group === "played"
        ? monthBucket(summary.lastPlayedAt)
        : group === "players"
          ? playerBucket(summary.game)
          : group === "weight"
            ? weightBucket(summary.game)
            : letterBucket(summary.game);

    const existing = buckets.get(b.key);
    if (existing) existing.games.push(summary);
    else {
      buckets.set(b.key, {
        key: b.key,
        label: b.label,
        hint: GROUP_HINTS[group],
        games: [summary],
        rank: b.rank,
      });
    }
  }

  return [...buckets.values()].sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// One game, in detail
// ---------------------------------------------------------------------------

export interface AspectRollup {
  /** The live aspect's id — the key that survives across session snapshots. */
  aspectId: string;
  name: string;
  kind: AspectKind;
  stops: Stop[];
  sort: number;
  profile: AspectProfile;
}

/**
 * How many nights the history radar draws. Five is what fits before the shapes
 * stop being separable from each other, ramp or no ramp.
 */
export const RADAR_NIGHTS = 5;

/** One spoke of the history radar, with the words its rungs mean. */
export interface RadarAxisRow {
  /** The live aspect's id, which is what survives across session snapshots. */
  id: string;
  name: string;
  kind: AspectKind;
  /** From the newest snapshot, so the vocabulary is the current one. */
  stops: Stop[];
}

export interface NightRow {
  sessionId: string;
  playedAt: number;
  /**
   * The night's value per axis id. Null where that night never asked the aspect,
   * or asked it and nobody answered — the radar bridges across either.
   */
  points: Record<string, number | null>;
}

export interface NightRadar {
  axes: RadarAxisRow[];
  /** Newest first. The screen paints the first one on top. */
  nights: NightRow[];
  /** In-scope completed nights that exist, so the caption can say what was cut. */
  available: number;
}

/**
 * One night's value on one axis: the median of the people who answered it.
 *
 * A median and not a mean, and this is the one place in the app that takes a
 * centre of a DESCRIPTIVE axis at all. `aspectProfile` nulls both centres for a
 * descriptive aspect on purpose (decision #25: a descriptive axis is never
 * averaged into anything), so this deliberately does not go through it.
 *
 * The rule it is honouring is about *scoring* — no mean of Luck ↔ Strategy rolls
 * up into a verdict about the game, and none does here either: nothing on the
 * radar is summed, ranked or compared across axes. What it needs is one point
 * per spoke per night, and the alternative to a centre is not plotting the
 * night's history at all. The median is the conservative pick of the two: one
 * person who saw a different game cannot drag the shape, which is exactly the
 * failure a mean of five opinions would have.
 *
 * The cost is real and worth naming: a night where two people said "Pure luck"
 * and two said "Pure skill" plots at the middle, which is a stop nobody chose.
 * That is what the per-aspect blocks below the chart are for — they still carry
 * every rater's own answer, and the split badge with them.
 */
function nightValue(cells: readonly RatingCell[]): number | null {
  const values: number[] = [];
  for (const cell of cells) {
    if (cell.na === 1 || cell.value === null) continue;
    values.push(cell.value);
  }
  if (values.length === 0) return null;
  return distribution(values).median;
}

export interface HeartRow {
  profile: Profile;
  /** Null when this player has never answered. Absent is not "no". */
  loved: boolean | null;
  row: PlayAgain | null;
}

export interface GameDetail {
  game: Game;
  /** Equal-weighted across completed sessions; `n` counts nights. */
  headline: Distribution | null;
  /** How good the evenings were, weighed the same way. */
  overall: Distribution | null;
  sessions: SessionSummary[];
  /** The rubric the profiles below were built from. */
  rubricVersion: number | null;
  /** Older rubric versions this game also has reviews under. Never merged in. */
  supersededVersions: number[];
  descriptive: AspectRollup[];
  evaluative: AspectRollup[];
  /** Widest spread first. The line the app exists to produce. */
  disagreements: Disagreement[];
  hearts: HeartRow[];
  /** The last few nights as polygons. Always present; may hold no nights. */
  radar: NightRadar;
}

/**
 * What each person's answer to each topic came to, worked out the same way the
 * rating screen does. Keyed by `session_aspect_id`, then by participant.
 *
 * A night is represented by everyone who *settled* the topic rather than by
 * everyone who happened to write a row in one table. The alternative was reading
 * `aspectRatings` alone, and it fails silently: a table where everybody went
 * into detail contributes zero cells, so the game's blocks show `n = 3` instead
 * of `n = 5`, the radar plots a night as null, and nothing anywhere reports a
 * missing input. `shared/aggregate.ts` holds the rule this defers to, including
 * the refusal to derive a DESCRIPTIVE axis.
 *
 * The participant is kept rather than discarded because the night's overall is
 * per-seat: it is the mean of the topics *one person* rated, so it cannot be
 * built from the flattened cells the aspect blocks want. This used to be two
 * near-identical walks over the same three tables, one throwing the seat away
 * and one keeping it — meaning the rule above lived twice, and a fix applied to
 * one copy gave a game page whose blocks and whose overall disagreed with
 * nothing reporting it.
 */
function topicValuesBySnapshot(lib: Library): Map<string, Map<string, TopicValue>> {
  const ownRows = byKey(live(lib.aspectRatings), (r) => r.session_aspect_id);
  const followUps = byKey(live(lib.sessionSubQuestions), (sq) => sq.session_aspect_id);
  const followUpRows = byKey(live(lib.subquestionRatings), (r) => r.session_subquestion_id);
  const kinds = new Map(live(lib.sessionAspects).map((sa) => [sa.id, sa.kind_snapshot]));

  const out = new Map<string, Map<string, TopicValue>>();
  for (const snapshotId of new Set([...ownRows.keys(), ...followUps.keys()])) {
    const own = new Map((ownRows.get(snapshotId) ?? []).map((r) => [r.participant_id, r]));

    const detail = new Map<string, RatingCell[]>();
    for (const sq of followUps.get(snapshotId) ?? []) {
      for (const row of followUpRows.get(sq.id) ?? []) {
        const list = detail.get(row.participant_id) ?? [];
        list.push({ value: row.value, na: row.na });
        detail.set(row.participant_id, list);
      }
    }

    const kind = kinds.get(snapshotId) ?? "EVALUATIVE";
    const bySeat = new Map<string, TopicValue>();
    for (const participantId of new Set([...own.keys(), ...detail.keys()])) {
      bySeat.set(
        participantId,
        topicValue(kind, own.get(participantId) ?? null, detail.get(participantId) ?? []),
      );
    }
    out.set(snapshotId, bySeat);
  }
  return out;
}

/**
 * The same values with the seat dropped, which is the shape every aggregate
 * over an aspect wants: one cell per person, pooled across the nights.
 */
function topicCellsBySnapshot(topics: Map<string, Map<string, TopicValue>>): Map<string, RatingCell[]> {
  const out = new Map<string, RatingCell[]>();
  for (const [snapshotId, bySeat] of topics) {
    const cells: RatingCell[] = [];
    for (const derived of bySeat.values()) {
      // Nothing at all is not a cell. An n/a is: "does not apply" is an answer,
      // and `aspectProfile` counts it separately from silence.
      if (derived.value === null && !derived.na) continue;
      cells.push({ value: derived.value, na: derived.na ? 1 : 0 });
    }
    out.set(snapshotId, cells);
  }
  return out;
}

export function buildGameDetail(lib: Library, gameId: string): GameDetail | null {
  const game = live(lib.games).find((g) => g.id === gameId);
  if (!game) return null;

  // One walk over every rating in the library, read twice: once per seat for the
  // nights' overalls, once pooled for the aspect blocks below.
  const topics = topicValuesBySnapshot(lib);
  const summary = gameSummary(
    game,
    completedSessions(lib, topics).filter((s) => s.session.game_id === gameId),
  );
  const sessions = summary.sessions;

  const versions = [...new Set(sessions.map((s) => s.session.rubric_version))].sort((a, b) => b - a);
  const current = versions[0] ?? null;
  const inScope = current === null ? [] : sessions.filter((s) => s.session.rubric_version === current);
  const scopeIds = new Set(inScope.map((s) => s.session.id));

  // Newest first, so the label and stops a rollup shows come from the most
  // recent snapshot rather than from whichever row IndexedDB happened to return.
  const snapshots = live(lib.sessionAspects)
    .filter((sa) => scopeIds.has(sa.session_id))
    .sort((a, b) => {
      const at = inScope.find((s) => s.session.id === a.session_id)?.session.played_at ?? 0;
      const bt = inScope.find((s) => s.session.id === b.session_id)?.session.played_at ?? 0;
      return bt - at;
    });

  const ratingsBySnapshot = topicCellsBySnapshot(topics);
  const rollups: AspectRollup[] = [];
  const slices: AspectSlice[] = [];

  for (const [aspectId, group] of byKey(snapshots, (sa) => sa.aspect_id)) {
    const newest = group[0];
    if (!newest) continue;

    // Already `RatingCell`s — the map used to hold `aspect_rating` rows and the
    // loop converted them. Re-wrapping them now would only make the next reader
    // look for the conversion that is no longer happening.
    const cells: RatingCell[] = group.flatMap((snapshot) => ratingsBySnapshot.get(snapshot.id) ?? []);

    const slice: AspectSlice = {
      id: aspectId,
      name: newest.name_snapshot,
      kind: newest.kind_snapshot,
      stops: newest.stops_snapshot,
      ratings: cells,
    };
    slices.push(slice);
    rollups.push({
      aspectId,
      name: slice.name,
      kind: slice.kind,
      stops: slice.stops,
      sort: newest.sort_snapshot,
      profile: aspectProfile(cells, slice.stops, slice.kind),
    });
  }

  const ordered = rollups
    .filter((r) => r.profile.distribution.n > 0 || r.profile.na > 0)
    .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));

  /**
   * The history radar: the last few nights, each as one polygon.
   *
   * Spokes are every aspect these nights actually asked, in the rubric's own
   * order — the same set the blocks below are built from, so the chart and the
   * detail never disagree about what the night covered. A night that did not
   * carry one of them has no snapshot for it and lands as null, which the radar
   * bridges rather than drawing as a bottom-of-the-ladder answer.
   *
   * `inScope` is already newest-first and already filtered to the current rubric
   * version, so the window inherits both rules for free: nights that asked
   * different questions are not plotted next to nights that asked these ones.
   */
  const plotted = inScope.slice(0, RADAR_NIGHTS);
  // Indexed over the plotted nights only. `snapshots` spans every night of this
  // rubric, which for a game played fifty times is fifty times the entries to
  // serve the same handful of lookups.
  const plottedIds = new Set(plotted.map((s) => s.session.id));
  const snapshotAt = new Map(
    snapshots
      .filter((sa) => plottedIds.has(sa.session_id))
      .map((sa) => [`${sa.session_id}:${sa.aspect_id}`, sa]),
  );
  const radar: NightRadar = {
    axes: ordered.map((r) => ({ id: r.aspectId, name: r.name, kind: r.kind, stops: r.stops })),
    nights: plotted.map((s) => {
      const points: Record<string, number | null> = {};
      for (const axis of ordered) {
        const snapshot = snapshotAt.get(`${s.session.id}:${axis.aspectId}`);
        points[axis.aspectId] = snapshot
          ? nightValue(ratingsBySnapshot.get(snapshot.id) ?? [])
          : null;
      }
      return { sessionId: s.session.id, playedAt: s.session.played_at, points };
    }),
    available: inScope.length,
  };

  return {
    game,
    headline: summary.headline,
    overall: summary.overall,
    sessions,
    rubricVersion: current,
    supersededVersions: versions.slice(1),
    descriptive: ordered.filter((r) => r.kind === "DESCRIPTIVE"),
    evaluative: ordered.filter((r) => r.kind === "EVALUATIVE"),
    disagreements: disagreement(slices),
    hearts: heartsFor(lib, gameId),
    radar,
  };
}

/**
 * The "would play again" heart, per profile.
 *
 * Only people who actually sat at the table for this game are listed — a heart
 * from someone who has never played it would be an opinion about a box.
 */
export function heartsFor(lib: Library, gameId: string): HeartRow[] {
  const sessionIds = new Set(
    live(lib.sessions)
      .filter((s) => s.game_id === gameId && s.status === "COMPLETED")
      .map((s) => s.id),
  );
  const played = new Set(
    live(lib.participants)
      .filter((p) => sessionIds.has(p.session_id))
      .map((p) => p.profile_id),
  );

  const rows = live(lib.playAgain).filter((p) => p.game_id === gameId);
  return live(lib.profiles)
    .filter((p) => played.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((profile) => {
      const row = rows.find((r) => r.profile_id === profile.id) ?? null;
      return { profile, loved: row === null ? null : row.value === 1, row };
    });
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatDate(at: number): string {
  const d = new Date(at);
  return `${d.getDate()} ${(MONTHS[d.getMonth()] ?? "").slice(0, 3)} ${d.getFullYear()}`;
}

/** "tonight" / "3 days ago" — the feed reads as a diary, not a log file. */
export function relativeDay(at: number, now: number = Date.now()): string {
  const days = Math.floor((now - at) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return plural(Math.floor(days / 7), "week");
  if (days < 365) return plural(Math.floor(days / 30), "month");
  return formatDate(at);
}

function plural(count: number, unit: string): string {
  return `${count} ${count === 1 ? unit : `${unit}s`} ago`;
}

/**
 * `n` is never optional. A 9.0 from one play is not a 9.0 from six, and the
 * unit matters as much as the number: nights and raters count different things.
 */
export function nLabel(n: number, unit: "night" | "rater" | "answer"): string {
  return `n = ${n} ${n === 1 ? unit : `${unit}s`}`;
}

/** The headline is a 1–10 track, so its strip needs ten stops to sit on. */
export const HEADLINE_STOPS: Stop[] = Array.from({ length: 10 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

/**
 * One dot per night, placed on the nearest whole stop of the 1–10 track.
 *
 * A game's headline is a distribution over session *means*, so any night the
 * table disagreed on lands on a float — 7.5, 7.666… — which matches none of the
 * integer stops the strip is drawn on. Handing those counts straight to the
 * strip renders an empty track under the hero figure: a bare mean with its
 * evidence missing, which is the one thing the headline tile may not be. Each
 * night is therefore bucketed to `Math.round` of its own mean, while the exact
 * game mean still shows as the strip's hairline. Nights nobody rated are not
 * bucketed anywhere — an unrated night is not a night at stop 0.
 */
export function nightlyCounts(sessions: readonly SessionSummary[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const s of sessions) {
    const mean = s.headline.mean;
    if (s.headline.n === 0 || mean === null) continue;
    const stop = Math.round(mean);
    counts.set(stop, (counts.get(stop) ?? 0) + 1);
  }
  return counts;
}

export { distribution };
export type { AspectProfile, Disagreement, Distribution, RatingCell };
